import * as React from "react"
import { useQuery } from "@tanstack/react-query"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { StatusBadge, StatusDot } from "@/components/ui/status"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { api } from "@/lib/api"
import { PageLoader, ErrorState } from "@/components/PageStates"
import { format } from "date-fns"
import {
  Activity,
  Database,
  HardDrive,
  Wifi,
  Clock,
  Zap,
  Server,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
} from "lucide-react"

function HealthPage() {
  const { data: health, isLoading, isError, refetch } = useQuery({
    queryKey: ["health"],
    queryFn: () => api.get("/health/dashboard"),
    refetchInterval: 10000,
  })

  const { data: realtime } = useQuery({
    queryKey: ["realtime"],
    queryFn: () => api.get("/monitoring/realtime"),
    refetchInterval: 5000,
  })

  const services = health?.data?.health?.services || []

  if (isLoading) return <PageLoader message="Loading health data..." />
  if (isError) return <ErrorState message="Failed to load health data." onRetry={() => refetch()} />

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Health Dashboard</h1>
          <p className="text-muted-foreground">System health and performance metrics</p>
        </div>
        <Button onClick={() => refetch()}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Overall Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            System Status
            <Badge variant={health?.data?.health?.status === "healthy" ? "success" : "destructive"}>
              {health?.data?.health?.score || 0}%
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {services.map((service) => (
              <div key={service.name} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <StatusDot status={service.status} />
                  <div>
                    <p className="font-medium">{service.label}</p>
                    <p className="text-sm text-muted-foreground">{service.message}</p>
                  </div>
                </div>
                <span className="text-sm text-muted-foreground">{service.latency}ms</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Real-time Metrics */}
      {realtime?.data && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Events/min</CardTitle>
              <Zap className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{realtime.data.eventsPerMinute}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Queue Pending</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{realtime.data.queuePending}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Users</CardTitle>
              <Wifi className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{realtime.data.activeUsers}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Service Details */}
      <Tabs defaultValue="d1">
        <TabsList>
          <TabsTrigger value="d1">D1 Database</TabsTrigger>
          <TabsTrigger value="kv">KV Store</TabsTrigger>
          <TabsTrigger value="panels">Panels</TabsTrigger>
          <TabsTrigger value="queue">Queue</TabsTrigger>
          <TabsTrigger value="jobs">Jobs</TabsTrigger>
        </TabsList>
        <TabsContent value="d1" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>D1 Database</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">Database health check details</p>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="kv" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>KV Store</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">KV store health check details</p>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="panels" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>VPN Panels</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">Panel health check details</p>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="queue" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Queue</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">Queue health check details</p>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="jobs" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Jobs</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">Job health check details</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default HealthPage
